import React, { useEffect, useState } from 'react';
import { Table, Card, Typography, Spin, Alert, Button, Statistic, Row, Col, Modal, Form, Input, Select, InputNumber, message, Popconfirm, Tabs } from 'antd';
import { ReloadOutlined, UserAddOutlined, DeleteOutlined } from '@ant-design/icons';
import api, { OwnershipEntry } from '../api';

const { Title, Text } = Typography;

interface OwnershipData {
  total_pool_cents: number;
  total_pool_dollars: string;
  as_of: string | null;
  entries: OwnershipEntry[];
}

const AdminOwnership: React.FC = () => {
  const [ownership, setOwnership] = useState<OwnershipData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteForm] = Form.useForm();
  const [inviteLoading, setInviteLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'active' | 'closed'>('active'); // S42: Tab state

  const fetchOwnership = async (status?: 'active' | 'closed') => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.ownership.getCurrent(status);
      setOwnership(data);
    } catch (err: any) {
      console.error('Failed to fetch ownership data:', err);
      setError(err.message || 'Failed to fetch ownership data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOwnership(activeTab);
  }, [activeTab]);

  const handleInviteSubmit = async (values: any) => {
    try {
      setInviteLoading(true);
      const response = await api.admin.createInvite({
        email: values.email,
        full_name: values.full_name,
        country: values.country,
        min_required_contribution_cents: Math.round(values.min_required_contribution_dollars * 100),
        locale: values.locale,
      });
      
      message.success(`Invitation sent to ${values.email}`);
      inviteForm.resetFields();
      setInviteModalOpen(false);
      
      // Show the invite code
      Modal.success({
        title: 'Invitation Sent',
        content: (
          <div style={{ wordBreak: 'break-all' }}>
            <p>Invitation has been sent to <strong>{values.email}</strong></p>
            <p style={{ marginTop: 16 }}>
              <strong>Invite Code:</strong>
            </p>
            <p style={{ 
              fontFamily: 'monospace', 
              fontSize: '16px',
              padding: '12px',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              borderRadius: '4px',
              wordBreak: 'break-all'
            }}>
              {response.invite.code}
            </p>
            <p style={{ marginTop: 16, fontSize: '14px', color: '#888' }}>
              Valid for {response.invite.expires_in_hours} hours
            </p>
          </div>
        ),
      });
    } catch (err: any) {
      message.error(err.message || 'Failed to send invitation');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleDeleteUser = async (userId: string, userName: string) => {
    try {
      await api.users.admin.delete(userId);
      message.success(`User ${userName} deleted successfully`);
      fetchOwnership(); // Refresh the table
    } catch (err: any) {
      message.error(err.message || 'Failed to delete user');
    }
  };

  const columns = [
    {
      title: 'User',
      dataIndex: 'full_name',
      key: 'full_name',
      sorter: (a: OwnershipEntry, b: OwnershipEntry) => a.full_name.localeCompare(b.full_name),
      render: (text: string, record: OwnershipEntry) => (
        <div>
          <div><strong>{text}</strong></div>
          <div style={{ fontSize: '0.85em', color: '#888' }}>{record.email}</div>
        </div>
      ),
    },
    {
      title: 'Base Principal',
      dataIndex: 'base_principal_dollars',
      key: 'base_principal_dollars',
      align: 'right' as const,
      sorter: (a: OwnershipEntry, b: OwnershipEntry) => 
        a.base_principal_cents - b.base_principal_cents,
      render: (text: string) => <Text strong>{text}</Text>,
    },
    {
      title: 'Ownership %',
      dataIndex: 'ownership_percent',
      key: 'ownership_percent',
      align: 'right' as const,
      sorter: (a: OwnershipEntry, b: OwnershipEntry) => {
        const percentA = parseFloat(a.ownership_percent.replace('%', ''));
        const percentB = parseFloat(b.ownership_percent.replace('%', ''));
        return percentA - percentB;
      },
      render: (text: string) => (
        <Text strong style={{ color: '#1890ff' }}>{text}</Text>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center' as const,
      render: (_: any, record: OwnershipEntry) => {
        // S42: Only show delete button for ineligible users (0% ownership) who are NOT exited
        // Exited users must be retained for 7 years
        const isExited = record.participation_status === 'exited';
        const hasZeroOwnership = record.base_principal_cents === 0;
        
        if (hasZeroOwnership && !isExited) {
          return (
            <Popconfirm
              title="Delete User"
              description={`Are you sure you want to delete ${record.full_name}? This action cannot be undone.`}
              onConfirm={() => handleDeleteUser(record.user_id, record.full_name)}
              okText="Yes, Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                size="small"
              >
                Delete
              </Button>
            </Popconfirm>
          );
        }
        return null;
      },
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        title="Error"
        description={error}
        type="error"
        showIcon
        action={
          <Button size="small" onClick={fetchOwnership}>
            Retry
          </Button>
        }
      />
    );
  }

  if (!ownership) {
    return (
      <Alert
        title="No Data"
        description="No ownership data available."
        type="info"
        showIcon
      />
    );
  }

  const totalPercent = ownership.entries.reduce((sum, entry) => {
    return sum + parseFloat(entry.ownership_percent.replace('%', ''));
  }, 0);

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={2}>Pool Ownership</Title>
        <div>
          <Button
            type="primary"
            icon={<UserAddOutlined />}
            onClick={() => setInviteModalOpen(true)}
            style={{ marginRight: 8 }}
          >
            Invite User
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => fetchOwnership(activeTab)}
            loading={loading}
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Total Pool Statistics */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <Card>
            <Statistic
              title="Total Pool"
              value={ownership.total_pool_dollars}
              precision={2}
              styles={{ content: { color: '#3f8600' } }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Active Users"
              value={ownership.entries.length}
              styles={{ content: { color: '#1890ff' } }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Sum of Ownership %"
              value={totalPercent.toFixed(4)}
              suffix="%"
              styles={{ 
                content: { 
                  color: Math.abs(totalPercent - 100) < 0.0001 ? '#3f8600' : '#cf1322' 
                }
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* Ownership Table with Tabs */}
      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as 'active' | 'closed')}
          items={[
            {
              key: 'active',
              label: `Active Users (${activeTab === 'active' ? ownership.entries.length : '...'})`,
            },
            {
              key: 'closed',
              label: `Closed Accounts (${activeTab === 'closed' ? ownership.entries.length : '...'})`,
            },
          ]}
        />
        <Table
          dataSource={ownership.entries}
          columns={columns}
          rowKey="user_id"
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} users`,
          }}
          footer={() => (
            <div style={{ textAlign: 'right', color: '#888', fontSize: '0.9em' }}>
              {ownership.as_of ? (
                <>Last updated: {new Date(ownership.as_of).toLocaleString()}</>
              ) : (
                <>No snapshot available</>
              )}
            </div>
          )}
        />
      </Card>

      {/* Warning if sum is not 100% */}
      {Math.abs(totalPercent - 100) > 0.0001 && (
        <Alert
          title="Ownership Sum Mismatch"
          description={`The sum of ownership percentages is ${totalPercent.toFixed(4)}%, not 100%. This may indicate a data integrity issue.`}
          type="warning"
          showIcon
          style={{ marginTop: 16 }}
        />
      )}

      {/* Invite User Modal */}
      <Modal
        title="Invite New User"
        open={inviteModalOpen}
        onCancel={() => {
          setInviteModalOpen(false);
          inviteForm.resetFields();
        }}
        onOk={() => inviteForm.submit()}
        confirmLoading={inviteLoading}
        okText="Send Invitation"
        maskClosable={false}
        styles={{
          mask: {
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
          },
          header: {
            backgroundColor: 'transparent !important' as any,
            borderBottom: 'none !important' as any,
            paddingBottom: '16px',
          },
          footer: {
            backgroundColor: 'transparent !important' as any,
            borderTop: 'none !important' as any,
            paddingTop: '16px',
          },
          body: {
            padding: '24px',
          },
        }}
      >
        <Form
          form={inviteForm}
          layout="vertical"
          onFinish={handleInviteSubmit}
        >
          <Form.Item
            name="email"
            label="Email Address"
            rules={[
              { required: true, message: 'Email is required' },
              { type: 'email', message: 'Please enter a valid email' },
            ]}
          >
            <Input placeholder="user@example.com" />
          </Form.Item>

          <Form.Item
            name="full_name"
            label="Full Name"
            rules={[{ required: true, message: 'Full name is required' }]}
          >
            <Input placeholder="John Doe" />
          </Form.Item>

          <Form.Item
            name="locale"
            label="Language"
            rules={[{ required: true, message: 'Language is required' }]}
            initialValue="en"
          >
            <Select>
              <Select.Option value="en">English</Select.Option>
              <Select.Option value="es">Español (Spanish)</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="country"
            label="Country"
            rules={[{ required: true, message: 'Country is required' }]}
            initialValue="USA"
          >
            <Select>
              <Select.Option value="USA">USA</Select.Option>
              <Select.Option value="NON_USA">Non-USA</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="min_required_contribution_dollars"
            label="Minimum Required Contribution"
            rules={[
              { required: true, message: 'Minimum contribution is required' },
              { type: 'number', min: 0, message: 'Amount must be positive' },
            ]}
            initialValue={0}
          >
            <InputNumber
              prefix="$"
              style={{ width: '100%' }}
              min={0}
              step={100}
              precision={2}
              placeholder="0.00"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminOwnership;
